import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAntDesignTheme } from '../config/theme';

const ThemeContext = createContext();

const THEME_MODE_KEY = 'keshavhrms_theme_mode';
const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get initial theme mode from localStorage or default to 'light'
  const getInitialThemeMode = () => {
    const saved = localStorage.getItem(THEME_MODE_KEY);
    if (saved && Object.values(THEME_MODES).includes(saved)) {
      return saved;
    }
    return THEME_MODES.LIGHT; // Default to light mode
  };

  // Determine if dark mode should be active based on theme mode
  const calculateIsDarkMode = (mode) => {
    if (mode === THEME_MODES.DARK) {
      return true;
    }
    if (mode === THEME_MODES.LIGHT) {
      return false;
    }
    // SYSTEM mode: check system preference
    if (mode === THEME_MODES.SYSTEM) {
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const initialMode = getInitialThemeMode();
    const initialDarkMode = calculateIsDarkMode(initialMode);
    // Set initial dark-mode class on body
    if (initialDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return initialDarkMode;
  });

  // Update dark mode state when theme mode changes
  useEffect(() => {
    const darkMode = calculateIsDarkMode(themeMode);
    setIsDarkMode(darkMode);
    
    // Add/remove dark-mode class on body for CSS dark mode detection (for edge cases like custom tag colors)
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [themeMode]);

  // Listen for system preference changes when in SYSTEM mode
  useEffect(() => {
    if (themeMode !== THEME_MODES.SYSTEM) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setIsDarkMode(e.matches);
      
      // Add/remove dark-mode class on body for CSS dark mode detection
      if (e.matches) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [themeMode]);

  const changeThemeMode = (mode) => {
    if (!Object.values(THEME_MODES).includes(mode)) {
      console.error('Invalid theme mode:', mode);
      return;
    }
    setThemeMode(mode);
    localStorage.setItem(THEME_MODE_KEY, mode);
  };

  const theme = getAntDesignTheme(isDarkMode);

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        isDarkMode,
        changeThemeMode,
        theme,
        THEME_MODES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export { THEME_MODES };

